/* eslint-disable react-hooks/rules-of-hooks */

import { useApolloClient } from '@apollo/react-hooks';
import type { ApolloClient } from 'apollo-client';
import giraClientConfig from '@atlassian/jira-apollo-gira';
// We use multiple different clients in this app, meaning
// we can't wrap the entire app in a single provider.
// eslint-disable-next-line jira/import/gql-multi-client
import { getClient, type ApolloClientConfig } from '@atlassian/jira-apollo-multiple-clients';
import swagClientConfig from '@atlassian/jira-apollo-swag';

import type { BaseUrl } from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

export const getGiraClient = (baseUrl: BaseUrl): ApolloClient<unknown> =>
	getClient(baseUrl, giraClientConfig);

export const getSwagClient = (baseUrl: BaseUrl) => getClient(baseUrl, swagClientConfig);

/**
 * WARNING: Do not enable this in production code! It's a dirty hack that lets us mock
 * Apollo responses in our examples. Apollo's `MockedProvider` only works if a client
 * is not explicitly passed into `useQuery`/`useMutation` calls. We have to explicitly
 * pass clients in because we use more than one of them.
 */
let shouldUseDefaultClient = false;
export const enableDefaultGraphQLClientForExamples = () => {
	shouldUseDefaultClient = true;
};

const useClient = (clientConfig: ApolloClientConfig): ApolloClient<unknown> => {
	const tenantContext = useTenantContext();
	return shouldUseDefaultClient
		? useApolloClient()
		: getClient(tenantContext.baseUrl, clientConfig);
};

export const useGiraClient = (): ApolloClient<unknown> => useClient(giraClientConfig);

export const useSwagClient = () => useClient(swagClientConfig);
